import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle2, XCircle, GitCompare } from 'lucide-react';
import content from '../../translations/content.json';
import './style.css';

const VocabDifference = () => {
  const { comparison } = content;
  const [hoveredRow, setHoveredRow] = useState(null);
  const [leftColumnVisible, setLeftColumnVisible] = useState(false);
  const [rightColumnVisible, setRightColumnVisible] = useState(false);

  const leftColumnRef = useRef(null);
  const rightColumnRef = useRef(null);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1, // Trigger when 10% of the element is visible
    };

    const leftObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setLeftColumnVisible(true);
          leftObserver.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const rightObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setRightColumnVisible(true);
          rightObserver.unobserve(entry.target);
        }
      });
    }, observerOptions);

    if (leftColumnRef.current) {
      leftObserver.observe(leftColumnRef.current);
    }
    if (rightColumnRef.current) {
      rightObserver.observe(rightColumnRef.current);
    }

    return () => {
      if (leftColumnRef.current) {
        leftObserver.unobserve(leftColumnRef.current);
      }
      if (rightColumnRef.current) {
        rightObserver.unobserve(rightColumnRef.current);
      }
    };
  }, []);

  return (
    <section className="vocab-difference-section">
      <div className="vocab-difference-container">
        <div className="vocab-difference-grid">
          <div
            ref={leftColumnRef}
            className={`vocab-difference-left-column ${leftColumnVisible ? 'fade-in-up' : 'opacity-0 translate-y-4'}`}
          >
            <p className="vocab-difference-subtitle">:: {comparison.subtitle}</p>
            <h2 className="vocab-difference-title">{comparison.title}</h2>
            <p className="vocab-difference-description">{comparison.description}</p>
            <ul className="vocab-difference-points">
              {comparison.points.map((point, index) => (
                <li key={index} className="vocab-difference-point">
                  <CheckCircle2 className="vocab-difference-point-icon" />
                  <span className="vocab-difference-point-text vocab-point-text-animated">{point}</span>
                </li>
              ))}
            </ul>
          </div>

          <div
            ref={rightColumnRef}
            className={`vocab-difference-right-column ${rightColumnVisible ? 'fade-in-up' : 'opacity-0 translate-y-4'}`}
          >
            <h3 className="vocab-difference-table-heading">
              <GitCompare className="inline-block w-6 h-6 mr-2 text-purple-500" />
              {comparison.table.heading}
            </h3>

            <div className="vocab-comparison-blocks-container">
              <div className="vocab-others-block">
                <div className="vocab-others-header">{comparison.table.othersHeader}</div>
                {comparison.table.rows.map((row, index) => (
                  <div
                    key={index}
                    className={`vocab-comparison-row ${hoveredRow === index ? 'hovered' : ''}`}
                    onMouseEnter={() => setHoveredRow(index)}
                    onMouseLeave={() => setHoveredRow(null)}
                  >
                    <div className="vocab-others-item">
                      <XCircle className="vocab-others-icon" />
                      {row.label}
                    </div>
                  </div>
                ))}
              </div>

              <div className="vocab-vocab-block">
                <div className="vocab-vocab-header">{comparison.table.vocabHeader}</div>
                {comparison.table.rows.map((row, index) => (
                  <div
                    key={index}
                    className={`vocab-comparison-row ${hoveredRow === index ? 'hovered' : ''}`}
                    onMouseEnter={() => setHoveredRow(index)}
                    onMouseLeave={() => setHoveredRow(null)}
                  >
                    <div className="vocab-vocab-item">
                      <CheckCircle2 className="vocab-vocab-icon" />
                      {row.vocab}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VocabDifference;
